import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { useMutation, useQuery } from '@apollo/client'
import { addDays, startOfDay } from 'date-fns'

import { validations } from '../form'
import { parseAndSetFormErrors, toAmountString } from '../../utils'
import { mutations, queries } from '../../graphql'
import { PAYMENT_TYPE } from '../../constants'
import {
  AdapterDateFns,
  Box,
  Button,
  Checkbox,
  DatePicker,
  Dialog,
  Fields,
  FormControl,
  FormControlLabel,
  Icons,
  InputAdornment,
  InputLabel,
  Link,
  LocalizationProvider,
  MenuItem,
  Row,
  Select,
  Stack,
  Text,
  useAlert,
  useDialog,
} from '..'

export function EditButton({ type, variables, mutation, refetchQueries, usedGiveGenius, linkedAmount, linkedTip }) {
  const { fundId } = useParams()
  const [{ dialogProps }, { open: openEditDialog, close: closeEditDialog }] = useDialog()

  return (
    <>
      <Button sx={{ background: 'none', p: 0, '&:hover': { background: 'none !important' } }} onClick={openEditDialog}>
        <Box sx={{ display: 'flex', color: 'primary.main' }}>
          <Icons.Edit />
          <Text.SR>Edit</Text.SR>
        </Box>
      </Button>
      <Dialog {...dialogProps}>
        <EditRecurringPaymentForm
          close={closeEditDialog}
          mutation={mutation}
          refetchQueries={refetchQueries}
          type={type}
          variables={variables}
          usedGiveGenius={usedGiveGenius}
          linkedAmount={linkedAmount}
          linkedTip={linkedTip}
        />
      </Dialog>
    </>
  )
}

function EditRecurringPaymentForm({
  close,
  mutation,
  type,
  refetchQueries,
  variables,
  usedGiveGenius,
  linkedAmount,
  linkedTip,
}) {
  const [recurringPaymentUpdated, setRecurringPaymentUpdated] = useState(false)
  const { register, handleSubmit, formState, setError, getValues, setValue, watch, control } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })
  const { isSubmitting, errors = {} } = formState
  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const linkedType = type === 'Gift' ? 'contribution' : 'gift'
  const [mutate] = useMutation(mutation, {
    onCompleted: () => {
      setRecurringPaymentUpdated(true)
      setAlert({
        message: usedGiveGenius
          ? `Recurring ${type} updated successfully. Your linked recurring ${linkedType} was updated too.`
          : `Recurring ${type} updated successfully.`,
        severity: 'success',
      })
    },
    onError: (newErrors) => {
      parseAndSetFormErrors(newErrors, setError)
      setAlert({ message: `Recurring ${type} update failed.`, severity: 'error' })
    },
    refetchQueries,
  })

  const {
    id,
    fundId,
    amount: existingAmount,
    tip: existingTip,
    nextScheduledGrant: existingNextScheduledGrant,
    nextScheduledTransfer: existingNextScheduledTransfer,
    nextScheduledCharge: existingNextScheduledCharge,
    frequency: existingFrequency,
    grantNote: existingGrantNote,
    anonymous: existingAnonymous,
    paymentMethodId: existingPaymentMethodId,
    paymentMethod: existingPaymentMethod,
  } = variables

  const parseAmountValue = (value) => {
    if (value === undefined || value === null || value === '') return null
    const parsed = typeof value === 'number' ? value : parseFloat(String(value).replaceAll(',', ''))
    return Number.isNaN(parsed) ? null : parsed
  }

  // Reactively track the amount (and, for Gifts, the tip) as the donor types, so the GiveGenius
  // banner below can preview what the linked record's amount will become after this edit.
  const watchedAmount = parseAmountValue(watch('amount') ?? existingAmount)
  const watchedTip = parseAmountValue(watch('tip') ?? existingTip) ?? 0
  let newLinkedAmount = null
  if (watchedAmount != null) {
    newLinkedAmount = type === 'Contribution' ? watchedAmount - (linkedTip ?? 0) : watchedAmount + watchedTip
  }
  const linkedAmountHasChanged =
    newLinkedAmount != null &&
    linkedAmount != null &&
    Math.round(newLinkedAmount * 100) !== Math.round(linkedAmount * 100)

  let linkedAmountPreviewText = ''
  if (linkedAmountHasChanged) {
    linkedAmountPreviewText = ` from ${toAmountString(linkedAmount)} to ${toAmountString(newLinkedAmount)}`
  } else if (linkedAmount != null) {
    linkedAmountPreviewText = ` (currently ${toAmountString(linkedAmount)})`
  }

  // Credit card selection for Contribution type
  const { data: cardData } = useQuery(queries.paymentMethods.myCreditCards, {
    skip: type !== 'Contribution' || !fundId,
    variables: { fundId },
  })
  const savedCards = cardData?.me?.fund?.creditCards?.filter((cc) => cc.status === 'active') || []
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(existingPaymentMethodId)
  const [updatePaymentMethod] = useMutation(mutations.contributions.updateRecurrentContributionPaymentMethod)

  // const grantOrTransfer = type === 'Gift' ? 'Grant' : 'Transfer'
  let activityType
  switch (type) {
    case 'Gift':
      activityType = 'Grant'
      break
    case 'Contribution':
      activityType = 'Contribution'
      break
    default:
      activityType = 'Transfer'
      break
  }

  const [datePickerValue, setDatePickerValue] = useState(
    existingNextScheduledGrant || existingNextScheduledTransfer || existingNextScheduledCharge
  )

  useEffect(() => setValue(`nextScheduled${activityType}`, datePickerValue), [datePickerValue, setValue])

  useEffect(() => {
    console.log('new datePickerValue:', datePickerValue)
  }, [datePickerValue])

  const minScheduledDate = startOfDay(addDays(new Date(), 1))

  const onSubmit = async (data) => {
    const {
      amount,
      tip,
      nextScheduledGrant,
      nextScheduledTransfer,
      nextScheduledCharge,
      frequency,
      grantNote,
      anonymous,
    } = data
    const mutateData = {
      id,
      frequency,
      amount: parseFloat(amount.replaceAll(',', ''), 10),
    }

    // Update payment method if changed (Contribution type only)
    if (type === 'Contribution' && selectedPaymentMethodId && selectedPaymentMethodId !== existingPaymentMethodId) {
      try {
        await updatePaymentMethod({
          variables: { id, fundId, paymentMethodId: selectedPaymentMethodId },
          refetchQueries: ['MyRecurring', 'MyCreditCards'],
        })
      } catch (e) {
        setAlert({ message: `Failed to update payment method.`, severity: 'error' })
        return
      }
    }

    mutate({
      variables: {
        data: (() => {
          switch (type) {
            case 'Gift':
              return {
                ...mutateData,
                fundId,
                tip: tip ? parseFloat(tip.replaceAll(',', ''), 10) : 0,
                nextScheduledGrant,
                grantNote,
                anonymous,
              }
            case 'Contribution':
              return {
                ...mutateData,
                fundId,
                nextScheduledCharge: datePickerValue,
              }
            default:
              return {
                ...mutateData,
                fundId,
                nextScheduledTransfer,
              }
          }
        })(),
      },
    })
  }
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack>
        <Text.H6>Update Recurring {type}</Text.H6>
        {recurringPaymentUpdated ? (
          <Button color="info" onClick={close}>
            Done
          </Button>
        ) : (
          <>
            <Stack mx={2}>
              {usedGiveGenius && (type === 'Contribution' || type === 'Gift') && (
                <Box>
                  <Text.Body>
                    This recurring {type === 'Contribution' ? 'contribution' : 'gift'} is linked to a recurring{' '}
                    {linkedType} through GiveGenius. Updating this amount will automatically update the linked{' '}
                    {linkedType}
                    {linkedAmountPreviewText} to keep them balanced.
                  </Text.Body>
                </Box>
              )}
              <Fields.Text
                label="Amount"
                name="amount"
                error={!!errors?.amount}
                helperText={errors?.amount?.message}
                InputProps={{
                  inputComponent: Fields.Amount,
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: {
                    setValue: (value) => setValue('amount', value),
                    getValue: () => getValues('amount'),
                    defaultValue: existingAmount,
                  },
                }}
                {...register('amount', {
                  ...{ min: { value: 1, message: 'Must be at least 1' } },
                  ...validations.required,
                  ...validations.number,
                })}
              />
              {type !== 'Shared Fund' && type !== 'Contribution' && (
                <Fields.Text
                  label="Tip"
                  name="tip"
                  error={!!errors?.tip}
                  helperText={errors?.tip?.message}
                  InputProps={{
                    inputComponent: Fields.Amount,
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    inputProps: {
                      setValue: (value) => setValue('tip', value),
                      getValue: () => getValues('tip'),
                      defaultValue: existingTip,
                    },
                  }}
                  {...register('tip', {
                    ...validations.number,
                  })}
                />
              )}
              <FormControl fullWidth>
                <InputLabel id="frequencyLabel">Frequency</InputLabel>
                <Select
                  value={getValues('frequency')}
                  onChange={(e) => {
                    setValue('frequency', e.target.value, { shouldValidate: true })
                  }}
                  label="Frequency"
                  labelId="frequencyLabel"
                  aria-label="Frequency"
                  error={!!errors.frequency?.province}
                  helpertext={errors?.frequency?.province?.message}
                  defaultValue={existingFrequency}
                  {...register('frequency', { ...validations.required })}
                >
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="quarterly">Quarterly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </Select>
              </FormControl>
              <Controller
                render={({ field }) => (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      {...field}
                      label={`Next Scheduled ${activityType}:`}
                      value={datePickerValue}
                      onChange={(newValue) => {
                        setDatePickerValue(typeof newValue === 'string' ? new Date(newValue) : newValue)
                      }}
                      disablePast
                      minDate={minScheduledDate}
                      renderInput={(params) => (
                        <Fields.Text
                          {...params}
                          {...register(`nextScheduled${activityType}`, {
                            validate: {
                              notInPast: (v) =>
                                new Date(v) >= minScheduledDate || 'Contribution date must not be in the past.',
                            },
                          })}
                          error={!!errors?.nextScheduledGrant}
                          helperText={errors?.nextScheduledGrant?.message}
                        />
                      )}
                    />
                  </LocalizationProvider>
                )}
                control={control}
                name={`Next Scheduled ${activityType}:`}
                defaultValue={datePickerValue}
              />
              {type === 'Contribution' &&
                existingPaymentMethod?.paymentType === PAYMENT_TYPE.card &&
                (savedCards.length > 1 ? (
                  <FormControl fullWidth>
                    <InputLabel id="paymentMethodLabel">Payment Method</InputLabel>
                    <Select
                      value={selectedPaymentMethodId || ''}
                      onChange={(e) => setSelectedPaymentMethodId(e.target.value)}
                      label="Payment Method"
                      labelId="paymentMethodLabel"
                      aria-label="Payment Method"
                    >
                      {savedCards.map(({ id: cardId, details: { card } }) => (
                        <MenuItem key={cardId} value={cardId}>
                          {card.brand.toUpperCase()} ending in {card.last4} (exp. {card.exp_month}/{card.exp_year})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Box>
                    <Text.Body>
                      To change your payment method,{' '}
                      <Link to={`/profile/${fundId}/payment-methods`}>add another card</Link>.
                    </Text.Body>
                  </Box>
                ))}
              {type !== 'Shared Fund' && type !== 'Contribution' && (
                <>
                  <Fields.Text
                    type="text"
                    label="Gift Note"
                    error={!!errors.grantNote}
                    helperText={errors?.grantNote?.message}
                    defaultValue={existingGrantNote}
                    {...register('grantNote')}
                  />
                  <FormControl>
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...register('anonymous')}
                          checked={getValues('anonymous') || existingAnonymous}
                          onChange={(e) => {
                            setValue('anonymous', e.target.checked, { shouldValidate: true })
                          }}
                        />
                      }
                      label="I wish to remain anonymous"
                    />
                  </FormControl>
                </>
              )}
            </Stack>
            <Row justifyContent="flex-end" alignItems="center" m={4}>
              <>
                <Button color="info" onClick={close} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button disabled={isSubmitting} type="submit">
                  Update
                </Button>
              </>
            </Row>
          </>
        )}
        <Alert {...alertProps} />
      </Stack>
    </form>
  )
}
